import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./registration_form.css";
import { useLocation } from "react-router-dom";

export function RegistrationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    travelers: 1,
    termsAccepted: false,
    privacyAccepted: false,
  });


  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const url = process.env.REACT_APP_API_URL;


// induló érték maradhat:
const { state } = useLocation();
const [tripTitle, setTripTitle] = useState(state?.title || state?.name || "");

// BASE_URL normalizálás (ne legyen dupla perjel / szóköz)
const BASE_URL = (url || "").trim().replace(/\/+$/, "");
const join = (base, path) => (base ? `${base}${path.startsWith("/") ? path : `/${path}`}` : path);

useEffect(() => {
  const endpoint = join(BASE_URL, `/api/utazasok/${encodeURIComponent(id)}`);
  let abort = false;

  (async () => {
    try {
      // debug (egy körre hasznos lehet):
      // console.log("GET", endpoint);

      const res = await fetch(endpoint, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // ha a szerver véletlenül HTML-t küld, ne dőljünk el
      const ct = res.headers.get("content-type") || "";
      const data = ct.includes("application/json") ? await res.json() : {};

      if (!abort) {
        const title = (data && (data.title || data.name)) || "";
        setTripTitle(title || `Utazás #${id}`);
      }
    } catch (e) {
      if (!abort) {
        // maradjon értelmes fallback, ne üres címsor
        setTripTitle((prev) => prev || `Utazás #${id}`);
        // console.error("Title fetch error:", e);
      }
    }
  })();

  return () => { abort = true; };
}, [id, BASE_URL]);


  const validateField = (name, value) => {
    // egyedi mezőellenőrzés
    switch (name) {
      case "name":
        if (!value.trim()) return "Kérjük, ezt is töltse ki.";
        return "";
      case "email":
        if (!value.trim()) return "Kérjük, ezt is töltse ki.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "Érvénytelen e-mail cím formátum.";
        return "";
      case "phone":
        if (!value.trim()) return "Kérjük, ezt is töltse ki.";
        // enyhe telefonellenőrzés (opcionális, finomítható)
        if (!/^[0-9+()\-\s]{6,}$/.test(value))
          return "Kérjük, valós telefonszámot adj meg.";
        return "";
      case "travelers": {
        const n = Number(value);
        if (!Number.isFinite(n)) return "Kérjük, számot adj meg.";
        if (n < 1 || n > 10) return "Az utazók száma 1 és 10 között lehet.";
        return "";
      }
      case "termsAccepted":
        if (!value) return "A jelentkezéshez el kell fogadnod az ÁSZF-et.";
        return "";
      case "privacyAccepted":
        if (!value) return "A jelentkezéshez el kell fogadnod az Adatvédelmi Nyilatkozatot.";
        return "";
      default:
        return "";
    }
  };

  const validateAll = () => {
    const fieldsToCheck = [
      "name",
      "email",
      "phone",
      "travelers",
      "termsAccepted",
      "privacyAccepted",
      // ha a "message" is legyen kötelező, add hozzá ide és a switch-hez:
      // "message"
    ];
    const newErrors = {};
    for (const field of fieldsToCheck) {
      const err = validateField(field, formData[field]);
      if (err) newErrors[field] = err;
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, type } = e.target;
    let value = e.target.value;

    if (type === "checkbox") value = e.target.checked;
    if (name === "travelers") value = Number(value); // mindig szám legyen

    const next = { ...formData, [name]: value };
    setFormData(next);

    // az adott mező hibáját azonnal frissítjük
    const fieldError = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: fieldError }));
    setSubmitError("");
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    const fieldError = validateField(name, formData[name]);
    setErrors((prev) => ({ ...prev, [name]: fieldError }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateAll();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = { travel_id: id, ...formData };
    try {
      const response = await fetch(`${url}/api/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        setSubmitError(errData.message || "Hiba történt az adatok küldésekor.");
        return;
      }
      await response.json();
      navigate("/koszonjuk");
    } catch (err) {
      setSubmitError("Nem sikerült csatlakozni a szerverhez. Kérlek, próbáld meg később.");
      console.error(err);
    }
  };

  return (
    <div className="registration-form-container">
      <h2>{tripTitle || `Utazás #${id}`}</h2>

      <form onSubmit={handleSubmit} noValidate>
        <div className={`form-field ${errors.name ? "has-error" : ""}`}>
          <label htmlFor="name">Neved:</label>
          <input
            id="name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "name-error" : undefined}
          />
          {errors.name && <div id="name-error" className="error">{errors.name}</div>}
        </div>

        <div className={`form-field ${errors.email ? "has-error" : ""}`}>
          <label htmlFor="email">E-mail:</label>
          <input
            id="email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "email-error" : undefined}
          />
          {errors.email && <div id="email-error" className="error">{errors.email}</div>}
        </div>

        <div className={`form-field ${errors.phone ? "has-error" : ""}`}>
          <label htmlFor="phone">Telefonszám:</label>
          <input
            id="phone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="+36 ..."
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? "phone-error" : undefined}
          />
          {errors.phone && <div id="phone-error" className="error">{errors.phone}</div>}
        </div>

        <div className={`form-field ${errors.travelers ? "has-error" : ""}`}>
          <label htmlFor="travelers">Utazók száma:</label>
          <input
            id="travelers"
            type="number"
            name="travelers"
            value={formData.travelers}
            onChange={handleChange}
            onBlur={handleBlur}
            min={1}
            max={10}
            aria-invalid={!!errors.travelers}
            aria-describedby={errors.travelers ? "travelers-error" : undefined}
          />
          {errors.travelers && <div id="travelers-error" className="error">{errors.travelers}</div>}
        </div>

        <div className="form-field">
          <label htmlFor="message">Üzenet:</label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            onBlur={handleBlur}
            rows={4}
            placeholder="Ide írd az üzeneted..."
          />
          {/* ha kötelezőre állítod a 'message'-t, itt is jeleníts meg hibát */}
          {errors.message && <div className="error">{errors.message}</div>}
        </div>

        {/* ÚJ: kötelező checkboxok kattintható linkekkel */}
        <div className={`form-field checkbox-field ${errors.termsAccepted ? "has-error" : ""}`}>
          <label>
            <input
              type="checkbox"
              name="termsAccepted"
              checked={formData.termsAccepted}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-invalid={!!errors.termsAccepted}
            />
            {" "}
            Elolvastam és elfogadom az{" "}
            <a href="/aszf" target="_blank" rel="noopener noreferrer">
              Általános szerződési feltételeket
            </a>.
          </label>
          {errors.termsAccepted && <div className="error">{errors.termsAccepted}</div>}
        </div>

        <div className={`form-field checkbox-field ${errors.privacyAccepted ? "has-error" : ""}`}>
          <label>
            <input
              type="checkbox"
              name="privacyAccepted"
              checked={formData.privacyAccepted}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-invalid={!!errors.privacyAccepted}
            />
            {" "}
            Elolvastam és elfogadom az{" "}
            <a href="/adatvedelmi_nyilatkozat" target="_blank" rel="noopener noreferrer">
              Adatvédelmi Nyilatkozatot
            </a>.
          </label>
          {errors.privacyAccepted && <div className="error">{errors.privacyAccepted}</div>}
        </div>

        {submitError && <div className="submit-error">{submitError}</div>}

        <button type="submit">Küldés</button>
      </form>
    </div>
  );
}